/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Menu } from 'antd';
import Icon from '@ant-design/icons';
import { isMenuItemRestricted, isNewFeatureTagShown } from './helper';
import { useFeatureFlag } from 'components/Providers/FeatureFlag';
import useTranslation from 'utils/hooks/useTranslation';

import styles from './style.module.scss';

const { SubMenu, Item: MenuItem } = Menu;

const MenuGenerator = ({ testid, theme, onMenuClick, menuItems, selectedKeys, isAdmin }) => {
  const { translateText } = useTranslation();
  const { showFeature } = useFeatureFlag();
  const { creditAccountMigrationConfig, corporationRole, currentCorporation } = useSelector(
    state => state.user,
  );

  const getLabel = menuItem => translateText(`corporateAccounts.menuLeft.${menuItem.key}`);

  const renderTitle = (menuItem, isIconDisplayed = true) => (
    <>
      {isIconDisplayed && menuItem.component && (
        <Icon component={menuItem.component} className={styles.icon} />
      )}
      <span className={styles.title}>{getLabel(menuItem)}</span>
      {creditAccountMigrationConfig?.creditAccount?.isNewBadgeDisplayed && menuItem.newFeature && (
        <span className={styles.newItemBadge}>{translateText('corporateAccounts.common.new')}</span>
      )}
      {isNewFeatureTagShown(menuItem, currentCorporation) && (
        <span className={styles.newFeatureTag}>
          {translateText('corporateAccounts.common.new')}
        </span>
      )}
    </>
  );

  const canShow = menuItem => {
    if (
      isMenuItemRestricted(
        menuItem,
        creditAccountMigrationConfig,
        isAdmin,
        currentCorporation?.country,
      )
    )
      return false;
    if (menuItem.feature && !showFeature(menuItem.feature)) return false;
    if (
      Array.isArray(menuItem.allowedRoles) &&
      menuItem.allowedRoles.length &&
      !isAdmin &&
      !menuItem.allowedRoles.includes(corporationRole)
    ) {
      return false;
    }
    return true;
  };

  const renderNode = (menuItem, isIconDisplayed = true) => {
    if (!canShow(menuItem)) return null;

    if (Array.isArray(menuItem.children) && menuItem.children.length > 0) {
      // First, check how many children are visible
      const visibleChildrenData = menuItem.children.filter(child => canShow(child));

      if (visibleChildrenData.length === 0) {
        return null;
      }

      // If only one child is visible, promote it to a main menu item (with icon)
      if (visibleChildrenData.length === 1) {
        return renderNode(visibleChildrenData[0], true);
      }

      // Multiple children - render as submenu with children without icons
      const visibleChildren = menuItem.children
        .map(child => renderNode(child, false))
        .filter(Boolean);

      return (
        <SubMenu key={menuItem.key} title={renderTitle(menuItem)}>
          {visibleChildren}
        </SubMenu>
      );
    }

    const linkContent = (
      <Link
        to={menuItem.url || '#'}
        data-testid={
          selectedKeys?.includes(menuItem.key) ? `selectedMenuItem-${menuItem.key}` : null
        }
      >
        {renderTitle(menuItem, isIconDisplayed)}
      </Link>
    );

    return (
      <MenuItem
        key={menuItem.key}
        disabled={menuItem.disabled || !menuItem.url}
        title={getLabel(menuItem)}
      >
        {linkContent}
      </MenuItem>
    );
  };

  return (
    <Menu
      data-testid={testid}
      theme={theme}
      onClick={onMenuClick}
      selectedKeys={selectedKeys}
      mode="inline"
      className={styles.navigation}
      // optional: control child indent uniformly
      // inlineIndent={24}
    >
      {menuItems.map(menuItem => renderNode(menuItem))}
    </Menu>
  );
};

export default MenuGenerator;
